import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ReviewCard from '@/components/base/ReviewCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { reviews } from '@/mocks/reviews';

const baseUrl = 'http://hn720.cn/';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading text-foreground-900">文章不存在</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);

  const schemaJson = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${baseUrl}/#website`,
        url: baseUrl,
        name: '吃瓜网在线',
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'Article',
        '@id': `${baseUrl}/article/${article.slug}/#article`,
        url: `${baseUrl}/article/${article.slug}`,
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.publishDate,
        image: article.imageUrl,
        articleSection: article.categoryName,
        inLanguage: 'zh-CN',
        isPartOf: { '@id': `${baseUrl}/#website` },
        keywords: article.tags.join(','),
        interactionStatistic: [
          { '@type': 'InteractionCounter', interactionType: 'https://schema.org/ReadAction', userInteractionCount: article.readCount },
          { '@type': 'InteractionCounter', interactionType: 'https://schema.org/CommentAction', userInteractionCount: article.commentCount },
          { '@type': 'InteractionCounter', interactionType: 'https://schema.org/ShareAction', userInteractionCount: article.shareCount },
        ],
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `${baseUrl}/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 吃瓜网在线`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},吃瓜网在线,在线吃瓜,51吃瓜,黑料吃瓜`}
        canonicalUrl={`${baseUrl}/article/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        schemaJson={schemaJson}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="bg-secondary-50/40">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-24 pb-10">
              <span className="inline-block text-xs bg-primary-500 text-background-50 px-3 py-1 rounded-full font-label mb-4">
                {article.categoryName}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 md:gap-5 text-xs text-foreground-500 font-label mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {(article.readCount / 10000).toFixed(0)}万 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i> {(article.commentCount / 10000).toFixed(1)}万 评论
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-background-200/60">
                <div className="text-center">
                  <div className="text-xl md:text-2xl font-heading font-bold text-secondary-600">
                    {(article.readCount / 10000).toFixed(0)}万
                  </div>
                  <div className="text-xs text-foreground-500 mt-0.5">阅读量</div>
                </div>
                <div className="text-center">
                  <div className="text-xl md:text-2xl font-heading font-bold text-secondary-600">
                    {(article.shareCount / 10000).toFixed(1)}万
                  </div>
                  <div className="text-xs text-foreground-500 mt-0.5">转发量</div>
                </div>
                <div className="text-center">
                  <div className="text-xl md:text-2xl font-heading font-bold text-secondary-600">
                    {(article.commentCount / 10000).toFixed(1)}万
                  </div>
                  <div className="text-xs text-foreground-500 mt-0.5">讨论数</div>
                </div>
                <div className="text-center">
                  <div className="text-xl md:text-2xl font-heading font-bold text-secondary-600">
                    实锤
                  </div>
                  <div className="text-xs text-foreground-500 mt-0.5">可信度</div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-10">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8">
                <div>
                  <Breadcrumb
                    items={[
                      { label: '首页', href: '/' },
                      { label: article.categoryName, href: `/category/${article.category}` },
                      { label: article.title },
                    ]}
                  />

                  <div className="mt-6">
                    <div className="w-full h-[300px] md:h-[400px] rounded-lg overflow-hidden mb-8">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <article
                      className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: article.content }}
                    />

                    <div className="mt-8 pt-6 border-t border-background-200/70">
                      <div className="flex flex-wrap gap-2">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full font-label"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {relatedArticles.length > 0 && (
                    <div className="mt-10">
                      <div className="flex items-center gap-2 mb-5">
                        <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                        <h3 className="text-lg font-heading font-bold text-foreground-900">
                          相关推荐
                        </h3>
                      </div>
                      <div className="flex flex-col gap-4">
                        {relatedArticles.map((ra) => (
                          <article
                            key={ra.slug}
                            className="flex flex-col sm:flex-row gap-4 p-4 bg-background-50 rounded-lg hover:bg-background-100 transition-colors group cursor-pointer"
                          >
                            <Link to={`/article/${ra.slug}`} className="shrink-0 w-full sm:w-[200px] h-[140px] rounded-lg overflow-hidden block cursor-pointer">
                              <img
                                src={ra.imageUrl}
                                alt={ra.title}
                                title={ra.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                loading="lazy"
                              />
                            </Link>
                            <div className="flex flex-col justify-between flex-1 min-w-0">
                              <div>
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="text-xs text-primary-500 bg-primary-100/70 px-2 py-0.5 rounded-full font-label whitespace-nowrap">
                                    {ra.categoryName}
                                  </span>
                                </div>
                                <Link to={`/article/${ra.slug}`} className="block cursor-pointer">
                                  <h4 className="text-base font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                                    {ra.title}
                                  </h4>
                                </Link>
                                <p className="text-sm text-foreground-600 mt-1.5 line-clamp-2 leading-relaxed">
                                  {ra.excerpt}
                                </p>
                              </div>
                              <div className="flex items-center gap-4 mt-3 text-xs text-foreground-500 font-label">
                                <span>{ra.publishDate}</span>
                                <span className="flex items-center gap-1">
                                  <i className="ri-eye-line"></i>
                                  {(ra.readCount / 10000).toFixed(1)}万
                                </span>
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-10">
                    <div className="flex items-center gap-2 mb-5">
                      <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
                      <h3 className="text-lg font-heading font-bold text-foreground-900">
                        精选评论
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {reviews.slice(0, 4).map((review, index) => (
                        <ReviewCard
                          key={review.id}
                          username={review.username}
                          avatar={review.avatar}
                          role={review.role}
                          rating={review.rating}
                          title={review.title}
                          content={review.content}
                          faded={index === 3}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <aside className="hidden lg:block">
                  <div className="sticky top-20">
                    <div className="bg-background-50 rounded-lg p-5 border border-background-200/70 mb-5">
                      <h3 className="text-base font-heading font-bold text-foreground-900 mb-4">
                        文章标签
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs bg-secondary-100/80 text-secondary-700 px-2.5 py-1 rounded-full font-label cursor-pointer hover:bg-secondary-200 transition-colors whitespace-nowrap"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="bg-primary-50 rounded-lg p-5">
                      <h3 className="text-base font-heading font-bold text-primary-700 mb-3">
                        分享这篇文章
                      </h3>
                      <p className="text-sm text-foreground-600 mb-4 leading-relaxed">
                        觉得有意思？分享给朋友们一起吃瓜！
                      </p>
                      <div className="flex gap-3">
                        <button className="w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                          <i className="ri-wechat-line"></i>
                        </button>
                        <button className="w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                          <i className="ri-weibo-line"></i>
                        </button>
                        <button className="w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                          <i className="ri-link"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>

          <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dramatic%20news%20background%20with%20warm%20amber%20and%20crimson%20glow%2C%20blurred%20effect%2C%20Chinese%20entertainment%20aesthetic%2C%20high%20contrast%2C%20editorial%20design%2C%20dark%20moody%20atmosphere%2C%20no%20text&width=1600&height=400&seq=cta-article-bg&orientation=landscape"
              alt="更多爆料"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
            <div className="absolute inset-0 flex items-center">
              <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
                <div className="max-w-md">
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-background-50 leading-tight mb-3">
                    还不过瘾？
                  </h2>
                  <p className="text-sm text-background-200 leading-relaxed mb-6">
                    查看更多热门爆料，精彩内容持续更新中
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-3 whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full font-label text-sm transition-colors cursor-pointer"
                  >
                    返回首页
                    <span className="w-7 h-7 rounded-full bg-background-950/30 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}